/**
 * Drives one Cloudflare Turnstile widget for a form. The challenge starts as
 * soon as the widget renders, so a token is usually ready by the time the
 * visitor submits; the widget is always drawn, so the visitor sees it pass,
 * ask for a click, or fail. Every token is single-use, so handing one out
 * immediately starts fetching the next.
 *
 * https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
 */

export interface TurnstileRenderOptions {
  sitekey: string
  action?: string
  language?: string
  execution?: 'render' | 'execute'
  appearance?: 'always' | 'execute' | 'interaction-only'
  theme?: 'auto' | 'light' | 'dark'
  size?: 'normal' | 'flexible' | 'compact'
  callback?: (token: string) => void
  'error-callback'?: (errorCode: string) => void
  'expired-callback'?: () => void
  'timeout-callback'?: () => void
  'unsupported-callback'?: () => void
  'before-interactive-callback'?: () => void
  'after-interactive-callback'?: () => void
}

/** The subset of `window.turnstile` this controller uses. */
export interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions,
  ) => string | undefined
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

/**
 * What the form should show: a token is ready to submit with, a challenge is
 * still under way, or the last one failed and no token is on hand.
 */
export type TurnstileState = 'verifying' | 'ready' | 'failed'

/**
 * How long a challenge may run without a word from the widget before it
 * counts as failed. A frame that cannot load (blocked by an extension or a
 * network filter) fires no callback at all, not even an error.
 */
export const STALL_TIMEOUT_MS = 15_000

export interface TurnstileControllerOptions {
  siteKey: string
  /** Echoed back by siteverify, so the server can tell a login token apart. */
  action: string
  /** ISO 639-1 code, or 'auto' to follow the visitor's browser. */
  language?: string
  /** Called whenever the state changes; it starts out 'verifying'. */
  onStateChange?: (state: TurnstileState) => void
}

export interface TurnstileController {
  /** Resolves with a fresh, single-use token, waiting for one if needed. */
  getToken: () => Promise<string>
  /** Removes the widget from the page; a pending getToken rejects. */
  dispose: () => void
}

interface PendingRequest {
  resolve: (token: string) => void
  reject: (error: Error) => void
}

export const createTurnstileController = (
  api: TurnstileApi,
  container: HTMLElement,
  {
    siteKey,
    action,
    language = 'auto',
    onStateChange,
  }: TurnstileControllerOptions,
): TurnstileController => {
  /** A token the challenge produced that nobody has taken yet. */
  let readyToken: string | null = null
  /** Why the last challenge failed, until a new one is started. */
  let failure: Error | null = null
  /** Whether a challenge is currently running. */
  let running = true
  let pending: PendingRequest | null = null
  /** The running challenge has gone quiet for STALL_TIMEOUT_MS. */
  let stalled = false
  let stallTimer: ReturnType<typeof setTimeout> | undefined
  let reportedState: TurnstileState = 'verifying'

  const report = () => {
    const state: TurnstileState = readyToken
      ? 'ready'
      : failure || stalled
        ? 'failed'
        : 'verifying'
    if (state === reportedState) return
    reportedState = state
    onStateChange?.(state)
  }

  const stopStallTimer = () => {
    if (stallTimer) clearTimeout(stallTimer)
  }

  const startStallTimer = () => {
    stopStallTimer()
    stalled = false
    stallTimer = setTimeout(() => {
      stalled = true
      report()
    }, STALL_TIMEOUT_MS)
  }

  const settle = (outcome: (request: PendingRequest) => void) => {
    const request = pending
    pending = null
    if (request) outcome(request)
  }

  const fail = (message: string) => {
    stopStallTimer()
    running = false
    failure = new Error(message)
    settle((request) => request.reject(failure as Error))
    report()
  }

  const widgetId = api.render(container, {
    sitekey: siteKey,
    action,
    language,
    execution: 'render',
    // Always drawn: a silent widget looks like a missing one, and a
    // configuration error (wrong hostname, bad key) shows up on the page.
    appearance: 'always',
    // The forms it sits in are white cards, whatever the OS colour scheme.
    theme: 'light',
    // Fills the container, so it lines up with the inputs above it.
    size: 'flexible',
    callback: (token) => {
      stopStallTimer()
      stalled = false
      running = false
      failure = null
      if (pending) {
        settle((request) => request.resolve(token))
      } else {
        readyToken = token
      }
      report()
    },
    // Turnstile refreshes an expired token by itself; just stop offering it.
    'expired-callback': () => {
      readyToken = null
      running = true
      startStallTimer()
      report()
    },
    'error-callback': (errorCode) =>
      fail(`Turnstile challenge failed (${errorCode})`),
    'timeout-callback': () => fail('Turnstile challenge timed out'),
    'unsupported-callback': () =>
      fail('Turnstile is not supported in this browser'),
    // A challenge waiting for the visitor's click has not stalled.
    'before-interactive-callback': stopStallTimer,
    'after-interactive-callback': startStallTimer,
  })

  if (!widgetId) {
    throw new Error('Turnstile widget could not be rendered')
  }
  startStallTimer()

  const startChallenge = () => {
    readyToken = null
    failure = null
    running = true
    startStallTimer()
    report()
    api.reset(widgetId)
  }

  return {
    getToken: () =>
      new Promise<string>((resolve, reject) => {
        if (readyToken) {
          const token = readyToken
          startChallenge()
          resolve(token)
          return
        }
        settle((request) =>
          request.reject(new Error('Turnstile challenge was restarted')),
        )
        pending = { resolve, reject }
        if (!running) startChallenge()
      }),
    dispose: () => {
      stopStallTimer()
      api.remove(widgetId)
      settle((request) =>
        request.reject(new Error('Turnstile widget was removed')),
      )
    },
  }
}
